import type { SyntheticEvent } from "react"
import { useState } from "react"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import {
  convertNormalizePriceToNumber,
  formatted,
  getPriceObjectWithTaxRate,
} from "@core/pricing"
import get from "lodash/get"
import type { SelectOption } from "@onestore/hel/dist/components/molecules/SelectMol/SelectMol.types"
import type { CloudBluePeriod } from "@gatsby-plugin-definitions/fragments/CloudBluePeriod"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { ExternalSupplierPrice } from "~/fragments/externalSupplierPrice"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import { safeFind } from "~/lib/collection"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import { getDefaultPlanData } from "./helpers/getDefaultPlanData"

type CalculatorPrices = {
  externalPrice?: string
  internalPrice: string | undefined
  savingsPrice: string | undefined
  periodText: string | undefined
}

export type HookResult = {
  activePlanDataId: number
  defaultConnectedControlsTitle: string | null | undefined
  defaultParametersTitle: string | undefined
  usersNumber: number
  externalSupplierPrice: ExternalSupplierPrice | undefined
  getProductName: () => string
  getConnectedControlsSelect: () => SelectOption[]
  getParametersSelect: () => SelectOption[]
  connectedProductSelectOnChange: (connectedProductSaleConfigId: string) => void
  parametersSelectOnChange: (
    productPlanDataAlias: CloudBluePlan["flatData"]["alias"]
  ) => void
  inputRangeOnChange: (usersNumber: number) => void
  inputNumberOnChange: (
    event: React.SyntheticEvent<HTMLInputElement, Event>,
    maxUsersNumber: number
  ) => void
  getActiveParameterName: () => string | undefined
  getCalculatorPrices: () => CalculatorPrices
  getButtonUrl: () => string | undefined
}

export default function useCostCalculator(
  saleConfiguration: SaleConfiguration,
  defaultCloudBluePlanData: CloudBluePlan["flatData"]
): HookResult {
  const { currentPriceType } = usePriceTypeContext()

  const [
    activeConnectedProductSaleConfig,
    setActiveConnectedProductSaleConfig,
  ] = useState<SaleConfiguration>(saleConfiguration)

  const [activeProductPlanData, setActiveProductPlanData] = useState<
    CloudBluePlan["flatData"]
  >(getDefaultPlanData(saleConfiguration, defaultCloudBluePlanData))

  const [usersNumber, setUsersNumber] = useState<number>(1)

  const defaultParametersTitle: string | undefined = get(
    saleConfiguration,
    "flatData.config.controls[0].title"
  )

  const getProductName = (): string => {
    const connectedProducts = saleConfiguration.flatData.connectedProducts ?? []

    const searchedProduct = connectedProducts.find(
      (product) =>
        get(product, "flatData.saleConfigurations[0].id") ===
        activeConnectedProductSaleConfig.id
    )

    return (
      searchedProduct?.flatData.name ||
      activeConnectedProductSaleConfig.flatData.name
    )
  }

  const getConnectedControlsSelect = (): SelectOption[] => {
    const connectedProducts = saleConfiguration.flatData.connectedProducts ?? []

    const connectedProductsData = connectedProducts
      .map((product) => {
        if (isEmpty(product.flatData.saleConfigurations)) {
          return null
        }

        const connectedProductName =
          product.flatData.variant || product.flatData.name

        const connectedProductSaleConfig =
          product.flatData.saleConfigurations[0]

        const defaultPlanExternalSupplierPrice = get(
          connectedProductSaleConfig,
          "flatData.defaultCloudBluePlan[0].flatData.externalSupplierPrice"
        )

        if (isEmpty(defaultPlanExternalSupplierPrice)) {
          return null
        }

        return {
          value: connectedProductSaleConfig.id,
          label: connectedProductName,
          isSelected: connectedProductSaleConfig.id === saleConfiguration.id,
        }
      })
      .filter(Boolean) as SelectOption[]

    return connectedProductsData
  }

  const getParametersSelect = (): SelectOption[] => {
    const controls = activeConnectedProductSaleConfig.flatData.config.controls
    const optionsMapping =
      activeConnectedProductSaleConfig.flatData.optionsMapping

    if (isEmpty(optionsMapping) || isEmpty(controls)) {
      return []
    }

    const parametersSelect = optionsMapping
      .map((optionMapping) => {
        const optionPlanExternalSupplierPrice = get(
          optionMapping,
          "cloudBluePlan[0].flatData.externalSupplierPrice"
        )

        const parameterAlias = get(
          optionMapping,
          "cloudBluePlan[0].flatData.alias"
        )

        const optionValueArray = optionMapping.value.match(/(.*)\[(\d+)\]/)

        if (
          isEmpty(optionValueArray) ||
          isEmpty(parameterAlias) ||
          isEmpty(optionPlanExternalSupplierPrice)
        ) {
          return null
        }

        const optionName = optionValueArray[1]
        const optionValue = optionValueArray[2]

        const searchedControl = controls.find(
          (control) => control.name === optionName
        )

        if (isEmpty(searchedControl)) {
          return null
        }

        const searchedControlOption = searchedControl.options.find(
          (option) => option.value === optionValue
        )

        if (isEmpty(searchedControlOption)) {
          return null
        }

        return {
          value: parameterAlias,
          label: searchedControlOption.name,
          isSelected: !!searchedControlOption.selected,
        }
      })
      .filter(Boolean) as SelectOption[]

    return parametersSelect
  }

  const getProductSaleConfig = (
    searchedProductSaleConfigId: string
  ): SaleConfiguration | undefined => {
    const connectedProductSaleConfigs =
      saleConfiguration.flatData.connectedProducts?.map(
        (connectedProduct) => connectedProduct.flatData.saleConfigurations[0]
      ) ?? []

    const allProductSaleConfigs = [
      saleConfiguration,
      ...connectedProductSaleConfigs,
    ]

    const searchedProductSaleConfig = allProductSaleConfigs.find(
      (productSaleConfig) =>
        productSaleConfig.id === searchedProductSaleConfigId
    )

    return searchedProductSaleConfig
  }

  const connectedProductSelectOnChange = (
    connectedProductSaleConfigId: string
  ): void => {
    const activeConnectedProductSaleConfig = getProductSaleConfig(
      connectedProductSaleConfigId
    )

    const activeProductDefaultPlanData = get(
      activeConnectedProductSaleConfig,
      "flatData.defaultCloudBluePlan[0].flatData"
    )

    if (
      !isEmpty(activeConnectedProductSaleConfig) &&
      !isEmpty(activeProductDefaultPlanData)
    ) {
      const defaultPlanData = getDefaultPlanData(
        activeConnectedProductSaleConfig,
        activeProductDefaultPlanData
      )

      setActiveConnectedProductSaleConfig(activeConnectedProductSaleConfig)
      setActiveProductPlanData(defaultPlanData)
    }
  }

  const parametersSelectOnChange = (
    productPlanDataAlias: CloudBluePlan["flatData"]["alias"]
  ): void => {
    if (isEmpty(activeConnectedProductSaleConfig.flatData.optionsMapping)) {
      return
    }

    const activeConfigOptionsMappingPlans: CloudBluePlan[] =
      activeConnectedProductSaleConfig.flatData.optionsMapping
        .map((option) =>
          !isEmpty(option.cloudBluePlan) ? option.cloudBluePlan[0] : undefined
        )
        .filter((item) => !isEmpty(item)) as CloudBluePlan[]

    const activeConfigDefaultCloudBluePlan: CloudBluePlan | undefined = get(
      activeConnectedProductSaleConfig,
      "flatData.defaultCloudBluePlan[0]",
      undefined
    )

    const allProductPlans = !isEmpty(activeConfigDefaultCloudBluePlan)
      ? [...activeConfigOptionsMappingPlans, activeConfigDefaultCloudBluePlan]
      : activeConfigOptionsMappingPlans

    const searchedProductPlanData = allProductPlans.find(
      (plan) => plan.flatData.alias === productPlanDataAlias
    )

    if (!isEmpty(searchedProductPlanData)) {
      setActiveProductPlanData(searchedProductPlanData.flatData)
    }
  }

  const inputRangeOnChange = (usersNumber: number): void => {
    setUsersNumber(usersNumber)
  }

  const inputNumberOnChange = (
    event: SyntheticEvent<HTMLInputElement, Event>,
    maxUsersNumber: number
  ): void => {
    if (isEmpty(event.currentTarget.value)) {
      setUsersNumber(1)
    } else {
      const value = parseInt(event.currentTarget.value)

      setUsersNumber(Math.max(1, Math.min(value, maxUsersNumber)))
    }
  }

  const getActivePlanPeriod = (): CloudBluePeriod | undefined => {
    const activeProductPlanAlias = activeProductPlanData.alias

    const activeOptionMapping =
      activeConnectedProductSaleConfig.flatData.optionsMapping?.find(
        (optionMapping) =>
          get(optionMapping, "cloudBluePlan[0].flatData.alias") ===
          activeProductPlanAlias
      )

    const activeOptionPeriodName = activeOptionMapping?.period?.period

    if (isEmpty(activeProductPlanData.periods)) {
      return undefined
    }

    const activePeriod = activeProductPlanData.periods.find(
      (period) => period.period_name === activeOptionPeriodName
    )

    const defaultPeriod = safeFind(
      activeProductPlanData.periods,
      (item) => item.default ?? false
    )

    return activePeriod || defaultPeriod
  }

  const getCalculatorPeriodText = (periodInfo: PeriodInfo): string => {
    if (periodInfo.isOneTimeFee()) {
      return _t("periodName.oneTimeFee")
    }

    if (periodInfo.hasMultipleCommitments()) {
      return periodInfo.getMultipleCommitmentsPeriodText()
    }

    const productPeriodValue = periodInfo.getPeriodLength()
    const productPeriodType = periodInfo.getPeriodType()

    return _t(`periodName.calculator.${productPeriodType}`, {
      smart_count: productPeriodValue,
    })
  }

  const getCalculatorPrices = (): CalculatorPrices => {
    const defaultPrices = {
      externalPrice: undefined,
      internalPrice: undefined,
      savingsPrice: undefined,
      periodText: undefined,
    }

    const externalSupplierPrice = activeProductPlanData.externalSupplierPrice

    const period = getActivePlanPeriod()

    const periodInfo = period ? new PeriodInfo(period) : null

    if (isEmpty(periodInfo) || isEmpty(externalSupplierPrice)) {
      return defaultPrices
    }

    const productPeriodName = periodInfo.getPeriodNameRaw()

    let productPeriodText = getCalculatorPeriodText(periodInfo)

    const selectedPeriodExternalPrice = externalSupplierPrice.staticPrices.find(
      (externalPrice) => externalPrice.period.period === productPeriodName
    )

    if (isEmpty(selectedPeriodExternalPrice)) {
      return defaultPrices
    }

    const productPriceValue = periodInfo.getRegisterPriceValue()
    const priceTaxRate = productPriceValue.taxRate

    const productPriceObject = getPriceObjectWithTaxRate(
      productPriceValue.netto * usersNumber,
      priceTaxRate
    )

    const productPriceFormatted = formatted(
      productPriceObject,
      currentPriceType
    )

    const externalPriceNumber = convertNormalizePriceToNumber(
      selectedPeriodExternalPrice.price
    )

    const externalPriceObject = getPriceObjectWithTaxRate(
      externalPriceNumber * usersNumber,
      priceTaxRate
    )

    const externalPrice = formatted(externalPriceObject, currentPriceType)

    const savingsPriceObject = getPriceObjectWithTaxRate(
      externalPriceNumber * usersNumber - productPriceValue.netto * usersNumber,
      priceTaxRate
    )

    const savingsPrice = formatted(savingsPriceObject, currentPriceType)

    return {
      externalPrice: externalPrice.formatted,
      internalPrice: productPriceFormatted.formatted,
      savingsPrice: savingsPrice.formatted,
      periodText: productPeriodText,
    }
  }

  const getActiveParameterName = () => {
    const parametersSelectData = getParametersSelect()

    const activeParameter = parametersSelectData.find(
      (parameter) => parameter.value === activeProductPlanData.alias
    )

    return activeParameter?.label
  }

  const getButtonUrl = (): string | undefined => {
    const acivePlanPeriod = getActivePlanPeriod()

    const hasQuantity = activeProductPlanData.hasQuantity

    const resources = {}

    const resourceId: number | undefined =
      activeProductPlanData.quantityResource?.id

    if (resourceId && usersNumber > 1) {
      resources[resourceId] = usersNumber
    }

    return url.generateBonusUrl({
      p: {
        alias: activeProductPlanData.alias,
        quantity: hasQuantity && usersNumber > 1 ? usersNumber : undefined,
        resources: !hasQuantity && !isEmpty(resources) ? resources : undefined,
      },
      ax: getUpsellData(activeConnectedProductSaleConfig.flatData.upsell),
      period: !isEmpty(acivePlanPeriod)
        ? acivePlanPeriod.period_name
        : undefined,
      priceType: currentPriceType,
    })
  }

  return {
    activePlanDataId: activeProductPlanData.id,
    defaultConnectedControlsTitle:
      saleConfiguration.flatData.connectedControlsTitle,
    defaultParametersTitle,
    usersNumber,
    externalSupplierPrice: activeProductPlanData.externalSupplierPrice,
    getProductName,
    getConnectedControlsSelect,
    getParametersSelect,
    connectedProductSelectOnChange,
    parametersSelectOnChange,
    inputRangeOnChange,
    inputNumberOnChange,
    getActiveParameterName,
    getCalculatorPrices,
    getButtonUrl,
  }
}
